/**
 * @component ry-tag-input
 * @category  form
 * @summary   Free-form tag entry (ry-ui-only). Type, press Enter / Tab / the delimiter character to commit a tag; Backspace at empty input removes the last tag. Paste-friendly — splits on the delimiter plus tabs/newlines. Tags render as removable `<ry-tag>` chips that emit `ry:remove` (handled internally to keep the model in sync). Form-submittable via a hidden input that mirrors the joined value.
 *
 * @attribute placeholder       string?              Input placeholder text. Default "Add tag...".
 * @attribute name              string?              Hidden input's `name` for native form submission.
 * @attribute value             string?              Initial tags as a delimiter-joined string (e.g. `"js,ts,css"`).
 * @attribute delimiter         string?   default="," Separator character used for: commit-on-keypress, paste splitting, hidden input join, initial value parse.
 * @attribute max-tags          string?              Maximum number of tags (parseInt). Once reached, new tag attempts silently no-op.
 * @attribute allow-duplicates  boolean   default=false   When absent, attempts to add a tag matching an existing one silently no-op. Set this to allow exact-text duplicates.
 * @attribute disabled          boolean   default=false   Non-interactive — input + tag removal both suppressed (the inner `<ry-tag removable>` still renders the X but it does nothing).
 *
 * @property  value    string    Read-only — current tags joined by the configured delimiter.
 * @property  values   string[]  Read-only — defensive copy of the current tag array.
 *
 * @event ry:beforeadd
 *        detail: { value: string }
 *        fires:  before a tag is committed (Enter, Tab, delimiter key, paste, initial `value` parse) — after the max-tags / duplicate rules pass, before the model, DOM, or hidden input change
 *        cancelable: preventDefault() rejects the tag; `ry:add` / `ry:change` do not fire and the typed text stays in the input so the user can edit it
 *
 * @event ry:add
 *        detail: { value: string }
 *        fires:  when a tag is committed (Enter, Tab, delimiter key, paste).
 *
 * @event ry:beforeremove
 *        detail: { value: string, label: string, index: number }
 *        fires:  before Backspace removes the last tag — before the model, DOM, or hidden input change. For the X-button path the inner `<ry-tag>`'s own `ry:beforeremove { value, label }` bubbles through the host: the same listener on `<ry-tag-input>` sees it, and preventDefault() keeps that chip (the inner `ry:remove` never fires, so the model is untouched).
 *        cancelable: preventDefault() keeps the tag; `ry:remove` / `ry:change` do not fire
 *
 * @event ry:remove
 *        detail: { value: string, label: string }
 *        fires:  ONLY for Backspace-removal of the last tag from the input. When the user clicks the `<ry-tag>`'s X button, the inner `<ry-tag>` emits its own `ry:remove` which ry-tag-input listens to + handles internally — the consumer-facing event for that path is `ry:change`. Watch `ry:change` for all tag-set mutations.
 *
 * @event ry:change
 *        detail: { values: string[] }
 *        fires:  on every tag-set mutation (add, remove, backspace, click-X). Defensive-copied — mutating `values` doesn't affect the component.
 *
 * @keyboard  Enter — commit current input text as a tag (skips if empty). Tab — commit if non-empty, else move focus naturally. `[delimiter]` key (default `,`) — commit. Backspace at empty input — remove the last tag.
 *
 * @content   None — `<ry-tag-input>` is configured entirely via attributes. innerHTML is replaced at setup with the rendered container + tags + input + hidden form mirror.
 *
 * @dom
 *   ry-tag-input[name?][value?][delimiter?][max-tags?][allow-duplicates?][disabled?]
 *     > div.ry-tag-input__container    [data-ry-target="container"]
 *       > div.ry-tag-input__tags        [data-ry-target="tags"]
 *         > ry-tag[removable][size="sm"][data-value]   (one per current tag)
 *       > input.ry-tag-input__input     [data-ry-target="input"]   type="text"
 *     > input    [data-ry-target="hidden"]   type="hidden"   name=name   value=joined-tags
 *
 * @gotcha  Duplicate detection is case-sensitive exact match on tag text. "JS" and "js" are different. Set `[allow-duplicates]` to disable detection entirely.
 *
 * @gotcha  Paste handling splits on the delimiter PLUS `\t` and `\n` regex characters. Pasting "a, b\nc\td" with default delimiter yields ["a", "b", "c", "d"]. Tabs and newlines are ALWAYS split, even with a non-comma delimiter.
 *
 * @gotcha  Tab key commits a non-empty input AND prevents tab navigation (preventDefault). If you type a tag and Tab to leave the field, the tag commits and focus stays. Empty Tab navigates normally. This is intentional but unusual — document for users.
 *
 * @gotcha  `ry:remove` fires ONLY for Backspace removal. The X-button-click path fires `ry:change` only (the inner `<ry-tag>`'s `ry:remove` is intercepted). Listen to `ry:change` for all mutations.
 *
 * @gotcha  Setting `el.value = "..."` does NOT work — there's no value setter. To programmatically replace tags, the component is read-only after mount. (Consider this when retrofitting consumers.)
 *
 * @example basic
 * ```html
 * <ry-tag-input placeholder="Add tags..." name="tags"></ry-tag-input>
 * ```
 *
 * @example pre-populated
 * ```html
 * <ry-tag-input value="js,ts,css" delimiter=","></ry-tag-input>
 * ```
 *
 * @example max-tags
 * ```html
 * <ry-tag-input max-tags="5" placeholder="Up to 5 tags"></ry-tag-input>
 * ```
 *
 * @example allow-duplicates
 * ```html
 * <ry-tag-input allow-duplicates value="apple,apple,banana"></ry-tag-input>
 * ```
 *
 * @example space-delimited
 * ```html
 * <ry-tag-input delimiter=" " placeholder="Type and Space"></ry-tag-input>
 * ```
 *
 * @tutorial mount-renders-input-and-tags-container
 * ```js
 * const el = mount('<ry-tag-input placeholder="x" name="tags"></ry-tag-input>');
 * await expectEqual(!!el.querySelector('[data-ry-target="container"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="tags"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="input"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="hidden"]'), true);
 * ```
 *
 * @tutorial initial-value-parses-into-tags
 * ```js
 * const el = mount('<ry-tag-input value="js,ts,css"></ry-tag-input>');
 * const tags = el.querySelectorAll('[data-ry-target="tags"] ry-tag');
 * await expectEqual(tags.length, 3, 'three tags rendered');
 * await expectEqual(el.values[0], 'js');
 * await expectEqual(el.values[2], 'css');
 * ```
 *
 * @tutorial enter-commits-input-as-tag
 * ```js
 * const el = mount('<ry-tag-input></ry-tag-input>');
 * const input = el.querySelector('[data-ry-target="input"]');
 * const fired = { add: [], change: [] };
 * el.addEventListener('ry:add', (e) => fired.add.push(e.detail.value));
 * el.addEventListener('ry:change', (e) => fired.change.push(e.detail.values));
 * input.value = 'hello';
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(el.values.length, 1);
 * await expectEqual(el.values[0], 'hello');
 * await expectEqual(input.value, '', 'input cleared after commit');
 * await expectEqual(fired.add[0], 'hello', 'ry:add fired');
 * await expectEqual(fired.change[0][0], 'hello', 'ry:change fired with new values');
 * ```
 *
 * @tutorial delimiter-key-commits
 * ```js
 * // Wrong assumption: only Enter/Tab commit. The delimiter character ALSO
 * // commits — typing the delimiter into the input is the most natural way
 * // to enter multiple tags in a row.
 * const el = mount('<ry-tag-input></ry-tag-input>');
 * const input = el.querySelector('[data-ry-target="input"]');
 * input.value = 'a';
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: ',', bubbles: true }));
 * await expectEqual(el.values[0], 'a');
 * ```
 *
 * @tutorial backspace-removes-last-tag
 * ```js
 * const el = mount('<ry-tag-input value="a,b,c"></ry-tag-input>');
 * const input = el.querySelector('[data-ry-target="input"]');
 * const fired = [];
 * el.addEventListener('ry:remove', (e) => fired.push(e.detail.value));
 * // Backspace on empty input
 * input.value = '';
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Backspace', bubbles: true }));
 * await expectEqual(el.values.length, 2);
 * await expectEqual(fired[0], 'c', 'last tag removed and ry:remove fired');
 * ```
 *
 * @tutorial backspace-with-text-does-not-remove
 * ```js
 * const el = mount('<ry-tag-input value="a,b"></ry-tag-input>');
 * const input = el.querySelector('[data-ry-target="input"]');
 * input.value = 'typing';
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Backspace', bubbles: true }));
 * await expectEqual(el.values.length, 2, 'tags untouched while typing');
 * ```
 *
 * @tutorial duplicate-detection-is-default
 * ```js
 * // Wrong assumption: typing the same tag twice adds two. By default,
 * // duplicates are silently rejected.
 * const el = mount('<ry-tag-input value="js"></ry-tag-input>');
 * const input = el.querySelector('[data-ry-target="input"]');
 * input.value = 'js';
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(el.values.length, 1, 'duplicate silently rejected');
 * ```
 *
 * @tutorial allow-duplicates-attribute
 * ```js
 * const el = mount('<ry-tag-input value="js" allow-duplicates></ry-tag-input>');
 * const input = el.querySelector('[data-ry-target="input"]');
 * input.value = 'js';
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(el.values.length, 2, 'duplicates allowed');
 * ```
 *
 * @tutorial max-tags-blocks-additions
 * ```js
 * const el = mount('<ry-tag-input value="a,b" max-tags="2"></ry-tag-input>');
 * const input = el.querySelector('[data-ry-target="input"]');
 * input.value = 'c';
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(el.values.length, 2, 'max-tags blocks additions');
 * ```
 *
 * @tutorial hidden-input-mirrors-joined-value-for-form-submission
 * ```js
 * const el = mount('<ry-tag-input name="tags" value="a,b,c"></ry-tag-input>');
 * const hidden = el.querySelector('[data-ry-target="hidden"]');
 * await expectAttr(hidden, 'name', 'tags');
 * await expectEqual(hidden.value, 'a,b,c', 'joined for form submission');
 * ```
 *
 * @tutorial beforeadd-veto
 * ```js
 * // preventDefault() on ry:beforeadd rejects the commit: the model, the
 * // chips, and the hidden input stay as they were, ry:add / ry:change stay
 * // silent, and the typed text is left in the input for the user to fix.
 * const el = mount('<ry-tag-input value="a"></ry-tag-input>');
 * const input = el.querySelector('[data-ry-target="input"]');
 * const added = [];
 * el.addEventListener('ry:add', (e) => added.push(e.detail.value));
 * el.addEventListener('ry:beforeadd', (e) => e.preventDefault(), { once: true });
 * input.value = 'b';
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(el.values.length, 1, 'vetoed add leaves the model alone');
 * await expectEqual(added.length, 0, 'ry:add did not fire');
 * await expectEqual(input.value, 'b', 'typed text is kept for editing');
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(el.values.length, 2, 'without the veto, Enter commits');
 * await expectEqual(added[0], 'b');
 * ```
 *
 * @tutorial beforeremove-veto
 * ```js
 * // One listener on the host vetoes both removal paths: Backspace (the
 * // host's own ry:beforeremove, with index) and the chip's X button (the
 * // inner <ry-tag>'s ry:beforeremove bubbling through).
 * const el = mount('<ry-tag-input value="a,b"></ry-tag-input>');
 * const input = el.querySelector('[data-ry-target="input"]');
 * const removed = [];
 * el.addEventListener('ry:remove', (e) => removed.push(e.detail.value));
 * let veto = true;
 * el.addEventListener('ry:beforeremove', (e) => { if (veto) e.preventDefault(); });
 * input.value = '';
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Backspace', bubbles: true }));
 * await expectEqual(el.values.length, 2, 'vetoed Backspace leaves both tags');
 * await expectEqual(removed.length, 0, 'ry:remove did not fire');
 * el.querySelector('[data-ry-target="tags"] ry-tag [data-ry-target="remove"]').click();
 * await expectEqual(el.values.length, 2, 'vetoed X click leaves both tags');
 * veto = false;
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Backspace', bubbles: true }));
 * await expectEqual(el.values.length, 1, 'without the veto, Backspace removes the last tag');
 * await expectEqual(removed[0], 'b');
 * ```
 *
 * @see ry-tag
 * @see ry-combobox
 */

import { RyElement } from '../core/ry-element.js';

export class RyTagInput extends RyElement {
  #tags: string[] = [];

  setup(): void {
    this.#transformMarkup();
    this.#parseInitialValue();
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="container"]')) return;

    const placeholder = this.getAttribute('placeholder') ?? 'Add tag...';
    const name = this.getAttribute('name') ?? '';
    const disabled = this.hasAttribute('disabled');

    this.innerHTML = `
      <div data-ry-target="container" class="ry-tag-input__container">
        <div data-ry-target="tags" class="ry-tag-input__tags"></div>
        <input data-ry-target="input" class="ry-tag-input__input" type="text" placeholder="${placeholder}" ${disabled ? 'disabled' : ''}>
      </div>
      <input data-ry-target="hidden" type="hidden" ${name ? `name="${name}"` : ''}>
    `;

    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (input) {
      this.on(input, 'keydown', this.#handleKeydown as EventListener);
      this.on(input, 'paste', this.#handlePaste as EventListener);
    }

    const container = this.$('[data-ry-target="container"]');
    if (container) {
      this.on(container, 'click', () => input?.focus());
    }

    this.on(this, 'ry:remove', this.#handleTagRemove as EventListener);
  }

  #parseInitialValue(): void {
    const value = this.getAttribute('value');
    if (!value) return;

    const delimiter = this.getAttribute('delimiter') ?? ',';
    const parts = value.split(delimiter).map(s => s.trim()).filter(Boolean);
    for (const tag of parts) {
      this.#addTag(tag);
    }
  }

  #handleKeydown = (e: KeyboardEvent): void => {
    const input = e.target as HTMLInputElement;
    const delimiter = this.getAttribute('delimiter') ?? ',';
    const val = input.value.trim();

    if (e.key === 'Enter' || e.key === delimiter || e.key === 'Tab') {
      if (val) {
        e.preventDefault();
        // A ry:beforeadd veto leaves the typed text in place for editing.
        if (this.#addTag(val)) input.value = '';
      } else if (e.key === 'Tab') {
        return; // allow natural tab
      }
    }

    if (e.key === 'Backspace' && !input.value && this.#tags.length > 0) {
      this.#removeLastTag();
    }
  };

  #handlePaste = (e: ClipboardEvent): void => {
    e.preventDefault();
    const text = e.clipboardData?.getData('text') ?? '';
    const delimiter = this.getAttribute('delimiter') ?? ',';
    const parts = text.split(new RegExp(`[${delimiter}\\t\\n]+`)).map(s => s.trim()).filter(Boolean);
    for (const tag of parts) {
      this.#addTag(tag);
    }
    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (input) input.value = '';
  };

  #handleTagRemove = (e: CustomEvent): void => {
    const value = (e.detail as { value: string }).value;
    const idx = this.#tags.indexOf(value);
    if (idx >= 0) {
      this.#tags.splice(idx, 1);
      this.#syncHidden();
      this.emit('change', { values: [...this.#tags] });
    }
  };

  /**
   * Commit one tag. Returns `false` only when a `ry:beforeadd` listener vetoed
   * it; `true` otherwise (added, or silently skipped by max-tags / duplicates).
   */
  #addTag(text: string): boolean {
    const maxTags = this.getAttribute('max-tags');
    if (maxTags && this.#tags.length >= parseInt(maxTags, 10)) return true;

    if (!this.hasAttribute('allow-duplicates') && this.#tags.includes(text)) return true;

    if (!this.emit('beforeadd', { value: text })) return false;

    this.#tags.push(text);

    const tagsContainer = this.$('[data-ry-target="tags"]');
    if (tagsContainer) {
      const tag = document.createElement('ry-tag');
      tag.setAttribute('removable', '');
      tag.setAttribute('size', 'sm');
      tag.setAttribute('data-value', text);
      tag.textContent = text;
      tagsContainer.appendChild(tag);
    }

    this.#syncHidden();
    this.emit('add', { value: text });
    this.emit('change', { values: [...this.#tags] });
    return true;
  }

  #removeLastTag(): void {
    const tagsContainer = this.$('[data-ry-target="tags"]');
    const lastTag = tagsContainer?.lastElementChild;
    if (lastTag) {
      const val = (lastTag as HTMLElement).dataset.value ?? '';
      if (!this.emit('beforeremove', { value: val, label: val, index: this.#tags.length - 1 })) return;
      lastTag.remove();
      this.#tags.pop();
      this.#syncHidden();
      this.emit('remove', { value: val, label: val });
      this.emit('change', { values: [...this.#tags] });
    }
  }

  #syncHidden(): void {
    const hidden = this.$<HTMLInputElement>('[data-ry-target="hidden"]');
    const delimiter = this.getAttribute('delimiter') ?? ',';
    if (hidden) {
      hidden.value = this.#tags.join(delimiter);
    }
  }

  get value(): string {
    const delimiter = this.getAttribute('delimiter') ?? ',';
    return this.#tags.join(delimiter);
  }

  get values(): string[] {
    return [...this.#tags];
  }
}

customElements.define('ry-tag-input', RyTagInput);
